import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { ArrowDownRight, ArrowUpRight, Calendar, Download, RefreshCw, Smartphone } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  fetchAppTrackingSummary,
  fetchAppTrackingTimeSpent,
  type AppTrackingSummaryRow,
  type TimeSpentRow,
} from '../../services/MasterAdmin/appTrackingInsightsService';
import InsightsPageShell from '../../components/MasterAdmin/insights/InsightsPageShell';
import InsightsStatCard from '../../components/MasterAdmin/insights/InsightsStatCard';
import InsightsChartCard, { INSIGHTS_CHART_TOOLTIP } from '../../components/MasterAdmin/insights/InsightsChartCard';
import InsightsSectionHeader from '../../components/MasterAdmin/insights/InsightsSectionHeader';
import { opsFilterClass, opsPrimaryBtn, opsSecondaryBtn, opsTableWrap } from '../../components/MasterAdmin/operations/operationsUi';
import { appTrackingMetricInfo } from '../../config/masterAdminMetricDefinitions';

function formatDuration(ms?: number): string {
  if (!ms || Number.isNaN(ms)) return '0s';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${min}m ${rem}s`;
}

function addDays(base: Date, days: number): Date {
  return new Date(base.getTime() + days * 24 * 60 * 60 * 1000);
}

function toDateInputValue(date: Date | null): string {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function parseDateInput(value: string): Date | null {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getDatesInRange(startDate: Date, endDate: Date): string[] {
  const dates: string[] = [];
  const current = new Date(startDate.getTime());
  current.setHours(0, 0, 0, 0);
  const end = new Date(endDate.getTime());
  end.setHours(0, 0, 0, 0);

  while (current <= end) {
    const year = current.getFullYear();
    const month = String(current.getMonth() + 1).padStart(2, '0');
    const day = String(current.getDate()).padStart(2, '0');
    dates.push(`${year}-${month}-${day}`);
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

function tickFormatter(val: string): string {
  if (!val || val === 'N/A') return '';
  const parts = val.split('-');
  if (parts.length >= 3) {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[parseInt(parts[1], 10) - 1]} ${parts[2]}`;
  }
  return val;
}

const AppTrackingInsightsPage: React.FC = () => {
  const [fromDate, setFromDate] = useState<Date | null>(addDays(new Date(), -30));
  const [toDate, setToDate] = useState<Date | null>(new Date());
  const [loading, setLoading] = useState(false);
  const [summaryRows, setSummaryRows] = useState<AppTrackingSummaryRow[]>([]);
  const [screenRows, setScreenRows] = useState<TimeSpentRow[]>([]);
  const [serviceRows, setServiceRows] = useState<TimeSpentRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [topN, setTopN] = useState<string>('10');

  const loadData = useCallback(async () => {
    if (!fromDate || !toDate || toDate < fromDate) return;
    setLoading(true);
    setError(null);
    try {
      const [summaryData, timeSpentData] = await Promise.all([
        fetchAppTrackingSummary(fromDate, toDate),
        fetchAppTrackingTimeSpent(fromDate, toDate),
      ]);
      setSummaryRows(summaryData.rows || []);
      setScreenRows(timeSpentData.screens || []);
      setServiceRows(timeSpentData.services || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Failed to load app tracking insights');
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const totals = useMemo(() => {
    return summaryRows.reduce(
      (acc, row) => {
        acc.installs += Number(row.install_count || 0);
        acc.uninstalls += Number(row.uninstall_count || 0);
        acc.netGrowth += Number(row.net_growth || 0);
        return acc;
      },
      { installs: 0, uninstalls: 0, netGrowth: 0 }
    );
  }, [summaryRows]);

  const chartRows = useMemo(() => {
    if (!fromDate || !toDate) return [];

    const lookup: Record<string, AppTrackingSummaryRow> = {};
    summaryRows.forEach((row) => {
      let dayVal = '';
      if (row.day) {
        if (typeof row.day === 'object' && (row.day as any).value) {
          dayVal = String((row.day as any).value);
        } else {
          dayVal = String(row.day);
        }
        dayVal = dayVal.split('T')[0];
        lookup[dayVal] = row;
      }
    });

    const dates = getDatesInRange(fromDate, toDate);

    return dates.map((dateStr) => {
      const match = lookup[dateStr];
      return {
        day: dateStr,
        install_count: match ? Number(match.install_count || 0) : 0,
        uninstall_count: match ? Number(match.uninstall_count || 0) : 0,
        net_growth: match ? Number(match.net_growth || 0) : 0,
      };
    });
  }, [summaryRows, fromDate, toDate]);

  const topScreenRows = useMemo(
    () =>
      [...screenRows]
        .sort((a, b) => Number(b.sessions_count || 0) - Number(a.sessions_count || 0))
        .slice(0, Number(topN)),
    [screenRows, topN]
  );

  const topServiceRows = useMemo(
    () =>
      [...serviceRows]
        .sort((a, b) => Number(b.sessions_count || 0) - Number(a.sessions_count || 0))
        .slice(0, Number(topN)),
    [serviceRows, topN]
  );

  const onRefresh = async () => {
    await loadData();
    if (!error) toast.success('Insights refreshed');
  };

  const exportChartRowsToCSV = () => {
    const headers = ['Day', 'Installs', 'Uninstalls', 'Net Growth'];
    const rows = chartRows.map((r) => [r.day, r.install_count, r.uninstall_count, r.net_growth]);
    const csvContent = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `app_tracking_${toDateInputValue(fromDate)}_to_${toDateInputValue(toDate)}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Toaster position="top-right" />
      <InsightsPageShell
        title="App Tracking Insights"
        description="Install, likely-uninstall, and time-spent analytics for the mobile app."
        info={appTrackingMetricInfo.page}
        actions={
          <>
            <label className={`${opsFilterClass} flex items-center gap-2`}>
              <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
              <input
                type="date"
                value={toDateInputValue(fromDate)}
                onChange={(e) => setFromDate(parseDateInput(e.currentTarget.value))}
                className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none dark:text-white"
              />
            </label>
            <label className={`${opsFilterClass} flex items-center gap-2`}>
              <Calendar className="h-4 w-4 shrink-0 text-gray-400" />
              <input
                type="date"
                value={toDateInputValue(toDate)}
                onChange={(e) => setToDate(parseDateInput(e.currentTarget.value))}
                className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 outline-none dark:text-white"
              />
            </label>
            <button type="button" onClick={() => void onRefresh()} disabled={loading} className={opsPrimaryBtn}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </>
        }
      >
        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300">
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
          <InsightsStatCard
            label="Installations"
            value={totals.installs.toLocaleString()}
            icon={Smartphone}
            loading={loading}
            info={appTrackingMetricInfo.installations}
          />
          <InsightsStatCard
            label="Likely Uninstalls"
            value={totals.uninstalls.toLocaleString()}
            icon={ArrowDownRight}
            accent="text-red-500"
            loading={loading}
            info={appTrackingMetricInfo.likelyUninstalls}
          />
          <InsightsStatCard
            label="Net User Growth"
            value={totals.netGrowth.toLocaleString()}
            icon={ArrowUpRight}
            accent={totals.netGrowth >= 0 ? 'text-emerald-500' : 'text-red-500'}
            loading={loading}
            info={appTrackingMetricInfo.netGrowth}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.4fr,1fr]">
          <InsightsChartCard title="Growth & Churn Trend" info={appTrackingMetricInfo.growthChurnTrend} heightClass="h-80">
            {loading ? (
              <div className="flex h-full items-center justify-center">
                <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartRows} margin={{ bottom: 15, left: 10, right: 10, top: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                  <XAxis
                    dataKey="day"
                    stroke="#94a3b8"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 10, fill: '#94a3b8', dy: 6 }}
                    tickFormatter={tickFormatter}
                  />
                  <YAxis stroke="#94a3b8" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <RechartsTooltip contentStyle={INSIGHTS_CHART_TOOLTIP} />
                  <Line type="monotone" dataKey="install_count" stroke="#3b82f6" strokeWidth={3} dot={{ r: 4 }} name="Installs" />
                  <Line type="monotone" dataKey="uninstall_count" stroke="#ef4444" strokeWidth={2} strokeDasharray="5 5" name="Uninstalls" />
                  <Line type="monotone" dataKey="net_growth" stroke="#10b981" strokeWidth={3} name="Net Growth" />
                </LineChart>
              </ResponsiveContainer>
            )}
          </InsightsChartCard>

          <InsightsChartCard title="Daily Acquisition Volume" info={appTrackingMetricInfo.acquisitionBar} heightClass="h-80">
            {loading ? (
              <div className="flex h-full items-center justify-center">
                <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartRows} margin={{ bottom: 15, left: 10, right: 10, top: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-20" vertical={false} />
                  <XAxis
                    dataKey="day"
                    stroke="#94a3b8"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fontSize: 10, fill: '#94a3b8', dy: 6 }}
                    tickFormatter={tickFormatter}
                  />
                  <YAxis stroke="#94a3b8" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                  <RechartsTooltip contentStyle={INSIGHTS_CHART_TOOLTIP} cursor={{ fill: 'rgba(148, 163, 184, 0.1)' }} />
                  <Bar dataKey="install_count" fill="#3b82f6" radius={[6, 6, 0, 0]} name="New Installs" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </InsightsChartCard>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-semibold text-gray-900 dark:text-white">Time Spent Breakdown</p>
          <div className="flex flex-wrap items-center gap-2">
            <select value={topN} onChange={(e) => setTopN(e.target.value)} className={opsFilterClass}>
              <option value="5">Top 5</option>
              <option value="10">Top 10</option>
              <option value="20">Top 20</option>
            </select>
            <button type="button" onClick={exportChartRowsToCSV} className={opsSecondaryBtn}>
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
            <InsightsSectionHeader title="Top Screens by Usage" info={appTrackingMetricInfo.screenTimeSpent} />
            <div className={opsTableWrap}>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-800/80 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-2">Screen</th>
                    <th className="px-4 py-2 text-right">Sessions</th>
                    <th className="px-4 py-2 text-right">Avg</th>
                    <th className="px-4 py-2 text-right">Median</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-12 text-center text-sm text-gray-500">
                        <RefreshCw className="mx-auto mb-2 h-6 w-6 animate-spin text-primary-600" />
                        Loading screens…
                      </td>
                    </tr>
                  ) : topScreenRows.length > 0 ? (
                    topScreenRows.map((row, idx) => (
                      <tr key={`${row.screen_name}-${idx}`} className="border-t border-gray-100 dark:border-gray-700/60">
                        <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-200">{row.screen_name || 'Unknown'}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-300">
                          {Number(row.sessions_count || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-300">
                          {formatDuration(Number(row.avg_duration_ms || 0))}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-300">
                          {formatDuration(Number(row.median_duration_ms || 0))}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-4 py-12 text-center text-sm text-gray-500">
                        No screen-time data
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800 sm:p-5">
            <InsightsSectionHeader title="Top Services by Usage" info={appTrackingMetricInfo.serviceTimeSpent} />
            <div className={opsTableWrap}>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-800/80 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-2">Service</th>
                    <th className="px-4 py-2 text-right">Sessions</th>
                    <th className="px-4 py-2 text-right">Avg</th>
                    <th className="px-4 py-2 text-right">Median</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={4} className="px-4 py-12 text-center text-sm text-gray-500">
                        <RefreshCw className="mx-auto mb-2 h-6 w-6 animate-spin text-primary-600" />
                        Loading services…
                      </td>
                    </tr>
                  ) : topServiceRows.length > 0 ? (
                    topServiceRows.map((row, idx) => (
                      <tr key={`${row.service_name}-${idx}`} className="border-t border-gray-100 dark:border-gray-700/60">
                        <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-200">{row.service_name || 'Unknown'}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-300">
                          {Number(row.sessions_count || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-300">
                          {formatDuration(Number(row.avg_duration_ms || 0))}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-300">
                          {formatDuration(Number(row.median_duration_ms || 0))}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-4 py-12 text-center text-sm text-gray-500">
                        No service-time data
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </InsightsPageShell>
    </>
  );
};

export default AppTrackingInsightsPage;
